import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router';
import config from '../utils/config';
import axiosInstance from '../utils/axiosInstance';
import DeviceDebug from './DeviceDebug';
import Button from './ui/Button';
import { Switch } from './pilot-ui/switch';
import S3BundleBrowser from './S3BundleBrowser';
import AppLogsSection from './devicedebug/AppLogsSection';
import { Device, DeviceDebugResponse } from '../types';
import { useFirestoreEnrichment } from '../hooks/useFirestoreEnrichment';
import { countDevicePhotos } from '../utils/devicePhotos';

interface DeviceDebugPanelProps {
  device: Device;
}

/**
 * Plan 94 Phase 2 - route-bound Device Debug body.
 *
 * Fetches debug data, app-user enrichment, the S3 bundle browser, and app logs for
 * one device, with no selector and no cross-page pin. The legacy `/debug` route
 * wraps this with a selector until Phase 6 redirects it into the workspace.
 */
const DeviceDebugPanel: React.FC<DeviceDebugPanelProps> = ({ device }) => {
  const deviceName = device.name;
  const { enrichmentMap } = useFirestoreEnrichment();
  const [debugData, setDebugData] = useState<DeviceDebugResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchDebugData = useCallback(async (
    name: string,
    opts?: { iotTrail?: boolean; iotTrailDays?: number },
  ) => {
    setLoading(true);
    setError('');
    try {
      let url = `${config.deviceDebugApiUrl}/${encodeURIComponent(name)}`;
      if (opts?.iotTrail) {
        const days = opts.iotTrailDays ?? 7;
        url += `?iot_trail=1&iot_trail_days=${days}`;
      }
      const response = await axiosInstance.get<DeviceDebugResponse>(url);
      setDebugData(response.data);
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { error?: string } } };
      if (axiosError?.response?.data?.error) {
        setError(axiosError.response.data.error);
      } else {
        setError('Failed to fetch debug data.');
      }
      setDebugData(null);
      console.error('Error fetching debug data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch (and reset) whenever the bound device changes.
  useEffect(() => {
    setDebugData(null);
    setError('');
    fetchDebugData(deviceName);
  }, [deviceName, fetchDebugData]);

  const handleRefresh = () => fetchDebugData(deviceName);

  // Auto-refresh
  useEffect(() => {
    if (autoRefresh) {
      intervalRef.current = setInterval(() => {
        fetchDebugData(deviceName);
      }, 60000);
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [autoRefresh, deviceName, fetchDebugData]);

  const enrichment = enrichmentMap[deviceName];

  return (
    <div className="space-y-2">
      <div className="bg-card border border-border rounded-lg shadow-md p-6">
        <div className="flex items-center gap-4 flex-wrap">
          <Button
            variant="primary"
            onClick={handleRefresh}
            disabled={loading}
            title="Fetch this device's debug data again: recent messages, readings and broker state"
          >
            {loading ? 'Loading...' : 'Refresh'}
          </Button>

          <label className="flex items-center gap-2 text-sm">
            <Switch
              checked={autoRefresh}
              onCheckedChange={setAutoRefresh}
              aria-describedby="auto-refresh-help"
            />
            Auto-refresh (60s)
          </label>
          <p id="auto-refresh-help" className="text-xs text-muted-foreground">Fetch updated device diagnostics every minute.</p>
        </div>

        {error && (
          <div role="alert" className="border border-status-error-border bg-status-error text-status-error-foreground p-3 rounded-md mt-4">
            {error}
          </div>
        )}

        {loading && !debugData && (
          <div className="text-muted-foreground mt-4">Loading debug data...</div>
        )}

        {enrichment && (() => {
          const hvacNames = enrichment.hvacSystems?.map(h => h.name).filter(Boolean).join(', ') || '';
          const filter = enrichment.filter;
          const filterParts: string[] = [];
          if (filter?.manufacturer) filterParts.push(filter.manufacturer);
          if (filter?.length && filter?.width && filter?.thickness) filterParts.push(`${filter.length}x${filter.width}x${filter.thickness}`);
          if (filter?.rating) filterParts.push(filter.rating);
          const photoCount = countDevicePhotos(enrichment);
          // Homeowner-facing filter %: the app renders airflowPercentageFloor
          // (Plan 26 monotonic floor) off the Firestore device doc.
          const floor = enrichment.device?.airflowPercentageFloor;
          const rawPct = enrichment.device?.airflowPercentage;
          const appFilterPct = floor != null
            ? `${Math.round(floor)}%${rawPct != null && Math.round(rawPct) !== Math.round(floor) ? ` (raw ${Math.round(rawPct)}%)` : ''}`
            : 'N/A';

          return (
            <div className="mt-4 p-4 bg-green-50 border border-status-success-border rounded-sm">
              <h3 className="text-sm font-semibold text-green-800 mb-2">App User Data</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                <div><span className="text-gray-600">User:</span> {enrichment.user?.email || 'N/A'}</div>
                <div><span className="text-gray-600">Location:</span> {enrichment.location?.name || 'N/A'}</div>
                <div><span className="text-gray-600">HVAC:</span> {hvacNames || 'N/A'}</div>
                <div><span className="text-gray-600">Filter:</span> {filterParts.join(' ') || 'N/A'}</div>
                <div>
                  <span className="text-gray-600">App Filter Life:</span>{' '}
                  <span className="font-semibold">{appFilterPct}</span>
                  <span className="text-gray-600"> (shown in app)</span>
                </div>
                <div>
                  <span className="text-gray-600">Filter Status:</span>{' '}
                  {filter?.status || 'N/A'}
                  {filter?.predictedChangeDate ? ` · change ${filter.predictedChangeDate}` : ''}
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-status-success-border">
                <Link
                  to={`/devices/${device.id}/photos`}
                  className="inline-flex items-center text-sm text-link hover:text-blue-800 hover:underline"
                >
                  View {photoCount} photo{photoCount !== 1 ? 's' : ''} for this device →
                </Link>
              </div>
            </div>
          );
        })()}
      </div>

      {debugData && (
        <DeviceDebug
          data={debugData}
          iotTrailLoading={loading}
          onLoadIoTTrail={(days: number) =>
            fetchDebugData(deviceName, { iotTrail: true, iotTrailDays: days })
          }
        />
      )}

      <S3BundleBrowser deviceName={deviceName} />

      <AppLogsSection
        deviceName={deviceName}
        userId={enrichment?.user?.id}
      />
    </div>
  );
};

export default DeviceDebugPanel;
